'use client';

import { useCallback, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useApiMutation } from '@shared/hooks/useApiMutation';
import { useApiInfiniteQuery } from '@shared/hooks/useInfiniteQuery';

import {
  type GetApplicationApiResponsePayload,
  getApplicationSuggestionsInfinite,
} from '@api/applications/general';
import {
  setSuggestionFunnyReaction,
  setSuggestionHelpfulnessReaction,
} from '@api/applications/reactions';

import { Activity, type ActivityHandle } from '@ui/uikit/components/common/Activity';
import { type FeedbackButtonGroupSelection } from '@ui/uikit/components/common/FeedbackButtonGroup';
import { PageSection } from '@ui/uikit/components/common/PageSection';
import { Typography } from '@ui/uikit/components/common/Typography';

import { ApplicationUserFeedbackCarousel } from '../../../../../components/ApplicationUserFeedback/ApplicationUserFeedbackCarousel';
import { FeedbackSectionButtons } from '../../../../../components/ApplicationUserFeedback/FeedbackSectionButtons';
import styles from './UserSuggestionsApplicationDetailsPageSection.module.scss';

interface UserSuggestionsApplicationDetailsPageSectionProps {
  application: GetApplicationApiResponsePayload;
}

const FEEDBACK_BUTTON_GROUP_SELECTION_TO_NUMERIC_VOTE_MAP: Partial<Record<FeedbackButtonGroupSelection, number>> = {
  'positive': 1,
  'negative': -1,
};

const UserSuggestionsApplicationDetailsPageSection = ({ application }: UserSuggestionsApplicationDetailsPageSectionProps) => {
  const { app_id: applicationSlug, app_name: applicationName } = application;

  const { data, isPending } = useApiInfiniteQuery(
    getApplicationSuggestionsInfinite,
    [applicationSlug],
  );

  const suggestions = data?.pages[0] || [];

  const { mutate: toggleHelpfulness } = useApiMutation(
    setSuggestionHelpfulnessReaction,
    applicationSlug,
  );
  const { mutate: setFunny } = useApiMutation(setSuggestionFunnyReaction, applicationSlug);

  const { user } = useAuth();
  const { open: openAuthModal } = useAuthModal();

  const activity = useRef<ActivityHandle>(null);
  const ensureAuth = useCallback(async () => activity.current?.initiate(), []);

  const queryClient = useQueryClient();

  const handleHelpfulnessToggle = useCallback(
    async (selection: FeedbackButtonGroupSelection, id: number) => {
      await ensureAuth();

      toggleHelpfulness({
        payload: {
          vote: FEEDBACK_BUTTON_GROUP_SELECTION_TO_NUMERIC_VOTE_MAP[selection] ?? 0
        },
        suggestionId: id,
        queryClient
      });
    },
    [ensureAuth, toggleHelpfulness, queryClient],
  );

  const handleFunnyReactionToggle = useCallback(
    async (state: 'on' | 'off', id: number) => {
      await ensureAuth();
      setFunny({ payload: { funny: state === 'on' }, suggestionId: id, queryClient });
    },
    [ensureAuth, setFunny, queryClient],
  );

  return (
    <PageSection
      id="#section-suggestions"
      variant="secondary"
      title={`${applicationName} Suggestions by Real Users`}
    >
      <Activity ref={activity}>
        <Activity.Step requirement={!!user} action={openAuthModal} key="signin" />
      </Activity>
      <div className={styles['container']}>
        <Typography variant="caption-s" color="secondary">
          You can be the star to make this app better! Write a suggestion to the developers and
          let&apos;s build together!
        </Typography>

        <FeedbackSectionButtons
          application={application}
          feedbackType="Suggestion"
          getItems={getApplicationSuggestionsInfinite}
          onFunnyReactionToggle={handleFunnyReactionToggle}
          onHelpfulnessToggle={handleHelpfulnessToggle}
          viewAllDisabled={suggestions?.length === 0}
        />

        <ApplicationUserFeedbackCarousel
          applicationSlug={applicationSlug}
          feedbackType="Suggestion"
          feedbackItems={suggestions}
          isPending={isPending}
          onHelpfulnessToggle={handleHelpfulnessToggle}
          onFunnyReactionToggle={handleFunnyReactionToggle}
        />
      </div>
    </PageSection>
  );
};

export {
  UserSuggestionsApplicationDetailsPageSection,
};
